import { createClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';
import { appStorage } from './appStorage';
import { safeNavigate } from '@/services/navigationService';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// --- SINGLETON GLOBAL ---
const globalAny = globalThis as any;
if (!globalAny.__supabaseClient) {
  globalAny.__supabaseClient = null;
}
// --- FIM SINGLETON GLOBAL ---

let supabaseClient: ReturnType<typeof createClient<Database>> | null = globalAny.__supabaseClient;
let isConnected = false;
let retryCount = 0;
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 segundos
let hasLoadedOnce = false;

// Variável para controlar se o listener já foi registrado
let onlineListenerRegistered = false;

// Estado reativo para listeners externos
let statusListeners: ((status: 'ok' | 'error') => void)[] = [];
let lastStatus: 'ok' | 'error' = 'ok';

function notifyStatus(status: 'ok' | 'error') {
  lastStatus = status;
  statusListeners.forEach(fn => fn(status));
}

export function subscribeSupabaseStatus(fn: (status: 'ok' | 'error') => void) {
  statusListeners.push(fn);
  fn(lastStatus); // já chama com o status atual
  return () => {
    statusListeners = statusListeners.filter(f => f !== fn);
  };
}

// Função para verificar conexão com a internet
function checkInternetConnection(): boolean {
  return navigator.onLine;
}

// Função para esperar um tempo específico
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Função para verificar e renovar a sessão
async function checkAndRefreshSession() {
  try {
    const { data: { session } } = await supabaseClient!.auth.getSession();
    if (session?.expires_at && (session.expires_at * 1000) - Date.now() < 30 * 60 * 1000) {
      await supabaseClient!.auth.refreshSession();
    }
  } catch (error) {
    console.warn('Erro ao verificar sessão:', error);
  }
}

// Função para criar o cliente Supabase com configurações específicas
function createSupabaseClient() {
  if (!supabaseClient) {
    supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        storage: appStorage
      }
    });
    globalAny.__supabaseClient = supabaseClient;

    // Verifica sessão quando app volta ao foco
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', checkAndRefreshSession);
    }
  }
  return supabaseClient;
}

// Exporta uma única instância do cliente
export const supabase = createSupabaseClient();

// Exporta a função testConnection para uso externo
export async function testConnection() {
  if (!checkInternetConnection()) {
    notifyStatus('error');
    return false;
  }

  for (let i = 0; i <= MAX_RETRIES; i++) {
    try {
      const { data, error } = await supabaseClient?.from('songs').select('id').limit(1);
      if (error) throw error;
      isConnected = true;
      retryCount = 0;
      notifyStatus('ok');
      return true;
    } catch (error) {
      if (i < MAX_RETRIES) {
        await wait(RETRY_DELAY);
      } else {
        notifyStatus('error');
        return false;
      }
    }
  }
  notifyStatus('error');
  return false;
}

// Função para tentar reconectar
export async function reconnect() {
  return testConnection();
}

// Função para verificar se está conectado
export function isSupabaseConnected() {
  return isConnected;
}

export function getPublicUrl(path: string) {
  try {
    const { data } = supabase.storage.from('songs').getPublicUrl(path);
    return data.publicUrl;
  } catch (error) {
    return '';
  }
}

export function formatStorageUrl(url: string): string {
  if (!url) {
    console.warn('URL vazia recebida em formatStorageUrl');
    return '';
  }
  
  // Se começa com data:, é uma imagem base64
  if (url.startsWith('data:')) {
    return url;
  }

  try {
    // Se já é uma URL completa do Supabase
    if (url.includes('supabase.co/storage/v1/object/public/')) {
      // Parse a URL completa
      const urlObj = new URL(url);
      
      // Pega o caminho após /public/
      const publicPath = urlObj.pathname.split('/public/')[1];
      
      if (!publicPath) {
        console.warn('URL do Supabase inválida:', url);
        return url;
      }

      // Limpa o caminho:
      // 1. Decode primeiro para evitar duplo encoding
      // 2. Remove múltiplas barras
      // 3. Remove espaços extras
      // 4. Encode novamente preservando barras
      const cleanPath = decodeURIComponent(publicPath)
        .replace(/\/+/g, '/') // Replace multiple slashes with single slash
        .trim()
        .replace(/\s+/g, ' '); // Replace multiple spaces with single space
      
      const encodedPath = encodeURIComponent(cleanPath).replace(/%2F/g, '/');
      
      // Reconstrói a URL mantendo o protocolo e domínio originais
      const finalUrl = `${urlObj.protocol}//${urlObj.host}/storage/v1/object/public/${encodedPath}`;
      
      return finalUrl;
    }

    // Se é uma URL relativa, constrói a URL completa
    const cleanPath = decodeURIComponent(url)
      .replace(/\/+/g, '/') // Replace multiple slashes with single slash
      .trim()
      .replace(/\s+/g, ' '); // Replace multiple spaces with single space
    
    const encodedPath = encodeURIComponent(cleanPath).replace(/%2F/g, '/');
    
    // Constrói a URL completa usando getPublicUrl
    const { data } = supabase.storage.from('songs').getPublicUrl(encodedPath);
    return data.publicUrl;
  } catch (error) {
    console.error('Erro ao formatar URL:', error, 'URL original:', url);
    return url;
  }
}

async function handleAuthParameters() {
  if (window.location.search) {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        safeNavigate('/home', { replace: true });
        return;
      }
      
      // Redirecionar para home após autenticação
      window.history.replaceState(null, '', window.location.pathname);
      safeNavigate('/home', { replace: true });
    } catch (error) {
      // Em caso de erro, redireciona para home
      safeNavigate('/home', { replace: true });
    }
  } else {
    // No tokens found in URL - silently continue
  }
}

// Função para cleanup do listener (para uso em testes ou hot reload)
export function cleanupSupabaseListeners() {
  if (onlineListenerRegistered && typeof window !== 'undefined') {
    window.removeEventListener('focus', checkAndRefreshSession);
    onlineListenerRegistered = false;
  }
}

// Listener para mudanças no estado da conexão
if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event, session) => {
    // Lógica de reconexão ou atualização de estado, se necessário
  });
} 